'use client'

import { useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import { Tooltip, Box, Grid, Typography, Chip, Card, useMediaQuery, Stack, Button, ButtonGroup } from '@mui/material'
import LinkIcon from '@mui/icons-material/Link'
import { useEffectAsync } from '../reactHelper'
import { useTranslation } from '../common/components/LocalizationProvider'
import PageLayout from '../common/components/PageLayout'
import SettingsMenu from './components/SettingsMenu'
import CollectionFab from './components/CollectionFab'
import CollectionActions from './components/CollectionActions'
import { formatTime, getStatusText, formatBoolean } from '../common/util/formatter'
import { useDeviceReadonly, useManager, useAdministrator } from '../common/util/permissions'
import useSettingsStyles from './common/useSettingsStyles'
import DeviceUsersValue from './components/DeviceUsersValue'
import usePersistedState from '../common/util/usePersistedState'
import DataTable from '../common/components/Datatable'
import { sessionActions } from '../store'
import { useTheme } from '@mui/material/styles'
import LogoImage from '../login/LogoImage'
import { nativePostMessage } from '../common/components/NativeInterface'
import { PieChart } from '@mui/x-charts/PieChart'
// Icons
import Diversity3Icon from "@mui/icons-material/Diversity3"
import GroupIcon from "@mui/icons-material/Group"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import DevicesIcon from "@mui/icons-material/Devices"
import TerrainIcon from "@mui/icons-material/Terrain"
import ErrorIcon from "@mui/icons-material/Error"
import HelpIcon from "@mui/icons-material/Help"
import AllInclusiveIcon from "@mui/icons-material/AllInclusive"
import LockIcon from "@mui/icons-material/Lock"
import LockOpenIcon from '@mui/icons-material/LockOpen'
import fetchOrThrow from '../common/util/fetchOrThrow';
import AddressValue from '../common/components/AddressValue';


// Novos estilos para impressão com gráficos
const printStyles = {
  container: {
    backgroundColor: '#ffffff',
    color: '#000000',
    padding: '10px',
    fontFamily: 'Arial, sans-serif',
  },
  header: {
    padding: 10,
    marginBottom: 20,
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '2px solid #ccc',
    paddingBottom: 10,
  },
  title: {
    fontSize: 24,
    margin: 0,
    color: '#000000',
    fontWeight: 'bold',
  },
  subtitle: {
    marginTop: 10,
    fontSize: 14,
    color: '#555555',
  },
  statsContainer: {
    display: 'flex',
    justifyContent: 'space-around',
    margin: '20px 0',
    flexWrap: 'wrap',
  },
  statCard: {
    border: '1px solid #ddd',
    borderRadius: 5,
    padding: 15,
    minWidth: 150,
    textAlign: 'center',
    margin: 10,
  },
  statValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#000',
  },
  statLabel: {
    fontSize: 14,
    color: '#555',
  },
  chartContainer: {
    margin: '20px 0',
    padding: 15,
    border: '1px solid #ddd',
    borderRadius: 5,
  },
  chartTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 15,
    color: '#000',
    textAlign: 'center',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: 20,
  },
  tableHead: {
    backgroundColor: '#f5f5f5',
  },
  tableHeadCell: {
    padding: '10px 15px',
    fontWeight: 'bold',
    color: '#000000',
    borderBottom: '2px solid #dddddd',
    textAlign: 'left',
  },
  tableCell: {
    padding: '10px 15px',
    color: '#000000',
    borderBottom: '1px solid #dddddd',
    textAlign: 'left',
  },
  statusOnline: {
    backgroundColor: '#4caf50',
    color: 'white',
    padding: '3px 8px',
    borderRadius: 4,
    fontSize: 12,
  },
  statusOffline: {
    backgroundColor: '#f44336',
    color: 'white',
    padding: '3px 8px',
    borderRadius: 4,
    fontSize: 12,
  },
  statusUnknown: {
    backgroundColor: '#ff9800',
    color: 'white',
    padding: '3px 8px',
    borderRadius: 4,
    fontSize: 12,
  },
  strong: {
    fontWeight: 'bold',
    color: '#000000',
  },
  filterChip: {
    margin: '0 5px 10px 0',
    backgroundColor: '#e0e0e0',
    color: '#000',
    fontWeight: 'bold',
  },
}

const PrintHeader = ({ title, deviceStatus, t }) => {
  return (
    <div style={printStyles.header}>
      <div style={printStyles.headerContent}>
        <LogoImage
          style={{ height: 60 }}
          onLoad={async () => {
            requestAnimationFrame(() =>
              requestAnimationFrame(() => {
                window.print()
                nativePostMessage(`print|${title}.pdf`)
              })
            )
          }}
          alt={t('companyLogo')}
        />
        <div>
          <h1 style={printStyles.title}>{title}</h1>
          <div style={printStyles.subtitle}>
            <div>
              <strong style={printStyles.strong}>{t('reportDate')}:</strong> {new Date().toLocaleString()}
            </div>
            {deviceStatus && (
              <div>
                <strong style={printStyles.strong}>{t('deviceStatusFilter')}:</strong> {getStatusText(deviceStatus, t)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const CustomPieLegend = ({ data, theme, t }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0)

  return (
    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
      <table style={{ borderCollapse: 'collapse' }}>
        <tbody>
          {data.map((item, index) => {
            const percentage = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0
            const color = [
              theme.palette.success.main,
              theme.palette.error.main,
              theme.palette.warning.main
            ][index]

            return (
              <tr key={index}>
                <td style={{ padding: '5px 10px' }}>
                  <div style={{
                    display: 'inline-block',
                    width: 15,
                    height: 15,
                    backgroundColor: color,
                    marginRight: 10
                  }}></div>
                </td>
                <td style={{ padding: '5px 10px', textAlign: 'left' }}>
                  <strong>{item.name}:</strong>
                </td>
                <td style={{ padding: '5px 10px', textAlign: 'right' }}>
                  {item.value} ({percentage}%)
                </td>
              </tr>
            )
          })}
          <tr>
            <td colSpan="2" style={{ padding: '5px 10px', textAlign: 'right', fontWeight: 'bold' }}>
              {t('deviceStatusTotal')}:
            </td>
            <td style={{ padding: '5px 10px', textAlign: 'right', fontWeight: 'bold' }}>
              {total}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

const DevicesPage = () => {
  const { classes } = useSettingsStyles()
  const navigate = useNavigate()
  const t = useTranslation()
  const dispatch = useDispatch()
  const theme = useTheme()
  const desktop = useMediaQuery(theme.breakpoints.up('md'))

  const groups = useSelector((state) => state.groups.items)
  const printing = useSelector((state) => state.session.printing)
  const manager = useManager()
  const deviceReadonly = useDeviceReadonly()
  const admin = useAdministrator()

  const positions = useSelector((state) => state.session.positions);

  const [timestamp, setTimestamp] = useState(Date.now())
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [showAll, setShowAll] = usePersistedState('showAllDevices', false)
  const [deviceStatus, setDeviceStatus] = useState('')
  const [deviceStatusChartData, setDeviceStatusChartData] = useState([])

  // Estatísticas para impressão
  const [deviceStats, setDeviceStats] = useState({
    total: 0,
    online: 0,
    offline: 0,
    unknown: 0,
  })

  useEffect(() => {
    const handleAfterPrint = () => {
      dispatch(sessionActions.setPrinting(false))
    }
    window.addEventListener('afterprint', handleAfterPrint)
    return () => window.removeEventListener('afterprint', handleAfterPrint)
  }, [dispatch])

    useEffectAsync(async () => {
      setLoading(true);
      try {
        const query = new URLSearchParams({ all: showAll });
        const response = await fetchOrThrow(`/api/devices?${query.toString()}`);
        const deviceData = await response.json();
        setItems(deviceData);

       // Calcular estatísticas
       const onlineCount = deviceData.filter(d => d.status === 'online').length;
       const offlineCount = deviceData.filter(d => d.status === 'offline').length;
       const unknownCount = deviceData.filter(d => !d.status || d.status === 'unknown').length;

      setDeviceStats({
        total: deviceData.length,
        online: onlineCount,
        offline: offlineCount,
        unknown: unknownCount,
      });

      setDeviceStatusChartData([
        { id: 0, name: t('deviceStatusOnline'), value: onlineCount },
        { id: 1, name: t('deviceStatusOffline'), value: offlineCount },
        { id: 2, name: t('deviceStatusUnknown'), value: unknownCount },
      ]);
      } finally {
        setLoading(false);
      }
    }, [timestamp, showAll]);

  const filteredDevices = useMemo(() => {
    if (!deviceStatus) return items;
    return items.filter(device => device.status === deviceStatus);
  }, [items, deviceStatus]);

    const handleExport = async () => {
      const data = filteredDevices.map((item) => ({
        [t('sharedName')]: item.name,
        [t('deviceIdentifier')]: item.uniqueId,
        [t('groupParent')]: item.groupId ? groups[item.groupId]?.name : null,
        [t('sharedPhone')]: item.phone,
        [t('deviceModel')]: item.model,
        [t('deviceContact')]: item.contact,
        [t('userExpirationTime')]: formatTime(item.expirationTime, 'date'),
        [t('positionAddress')]: positions[item.id]?.address || '',
      }));

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(t('deviceTitle'));
    const headers = Object.keys(data[0]);

    const titleRow = worksheet.addRow([t('deviceTitle')]);
    worksheet.mergeCells(1, 1, 1, headers.length);
    titleRow.font = { bold: true };

    const border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.border = border;
      cell.font = {};
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: `FF${theme.palette.primary.main.replace('#', '').toUpperCase()}` },
      };
    });
    data.forEach((item) => {
      const row = worksheet.addRow(headers.map((h) => item[h]));
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = border;
        cell.font = {};
      });
    });

    const blob = new Blob(
      [await workbook.xlsx.writeBuffer()],
      { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
    );
    saveAs(blob, 'devices.xlsx');
  };

  const handlePdfExport = () => {
    dispatch(sessionActions.setPrinting(true))
  }

  const actionConnections = {
    key: 'connections',
    title: t('sharedConnections'),
    icon: <LinkIcon fontSize='small' />,
    handler: (deviceId) => navigate(`/settings/device/${deviceId}/connections`)
  }

  const columns = [
    { field: 'id', headerName: 'ID', width: 80, disableColumnMenu: true },
    { field: 'name', headerName: t('sharedName'), flex: 1, disableColumnMenu: true },
    { field: 'uniqueId', headerName: t('deviceIdentifier'), flex: 1, disableColumnMenu: true },
    {
      field: 'groupId',
      headerName: t('groupParent'),
      width: 140,
      disableColumnMenu: true,
      renderCell: (params) => groups[params.row.groupId]?.name || params.row.groupId
    },
    { field: 'phone', headerName: t('sharedPhone'), flex: 1, disableColumnMenu: true },
    { field: 'model', headerName: t('deviceModel'), flex: 1.2, disableColumnMenu: true },
    { field: 'contact', headerName: t('deviceContact'), flex: 1, disableColumnMenu: true },
    {
      field: 'expirationTime',
      headerName: t('userExpirationTime'),
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => formatTime(params.row.expirationTime, 'date')
    },
    {
      field: 'status',
      headerName: t('deviceStatus'),
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => {
        const status = params.row.status || 'unknown';
        return (
          <Chip
            label={getStatusText(status, t)}
            size="small"
            style={status === 'online' ? printStyles.statusOnline :
                   status === 'offline' ? printStyles.statusOffline :
                   printStyles.statusUnknown}
          />
        );
      }
    },
    {
      field: 'ignition',
      headerName: t('positionIgnition'),
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => {
        const ignition = params.row.position?.attributes?.ignition;
        return ignition !== undefined ? (
          <Chip
            label={formatBoolean(ignition, t)}
            size="small"
            style={{
              backgroundColor: ignition ? theme.palette.success.main : theme.palette.error.main,
              color: 'white'
            }}
          />
        ) : t('sharedUnknown');
      }
    },
    {
      field: 'settingsUsers',
      headerName: t('settingsUsers'),
      sortable: false,
      renderCell: (params) => <DeviceUsersValue deviceId={params.row.id} />,
      disableColumnMenu: true,
      hide: !manager
    },
    {
      field: 'address',
      headerName: t('positionAddress'),
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => positions[params.row.id]?.address || t('sharedUnknown')
    },
    {
      field: 'actions',
      headerName: t('actions'),
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <CollectionActions
            itemId={params.row.id}
            editPath='/settings/device'
            endpoint='devices'
            setTimestamp={setTimestamp}
            customActions={[actionConnections]}
            readonly={deviceReadonly}
          />
        </Box>
      ),
      width: 160,
      disableColumnMenu: true
    }
  ]

  if (printing) {
    return (
      <div style={printStyles.container}>
        <PrintHeader title={t('deviceTitle')} deviceStatus={deviceStatus} t={t} />

        {/* Estatísticas resumidas */}
        <div style={printStyles.statsContainer}>
          <Card style={printStyles.statCard}>
            <Typography style={printStyles.statValue}>{deviceStats.total}</Typography>
            <Typography style={printStyles.statLabel}>{t('deviceTotal')}</Typography>
          </Card>
          <Card style={printStyles.statCard}>
            <Typography style={{...printStyles.statValue, color: theme.palette.success.main}}>
              {deviceStats.online}
            </Typography>
            <Typography style={printStyles.statLabel}>{t('deviceStatusOnline')}</Typography>
          </Card>
          <Card style={printStyles.statCard}>
            <Typography style={{...printStyles.statValue, color: theme.palette.error.main}}>
              {deviceStats.offline}
            </Typography>
            <Typography style={printStyles.statLabel}>{t('deviceStatusOffline')}</Typography>
          </Card>
          <Card style={printStyles.statCard}>
            <Typography style={{...printStyles.statValue, color: theme.palette.warning.main}}>
              {deviceStats.unknown}
            </Typography>
            <Typography style={printStyles.statLabel}>{t('deviceStatusUnknown')}</Typography>
          </Card>
        </div>

        {/* Gráfico de status */}
        <div style={printStyles.chartContainer}>
          <Typography style={printStyles.chartTitle}>
            {t('devicesStatusDistribution')}
          </Typography>
          {deviceStatusChartData.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <PieChart
                series={[{
                  data: deviceStatusChartData,
                }]}
                width={400}
                height={300}
                colors={[
                  theme.palette.success.main,
                  theme.palette.error.main,
                  theme.palette.warning.main
                ]}
              />
            </div>
          )}
          <CustomPieLegend
            data={deviceStatusChartData}
            theme={theme}
            t={t}
          />
        </div>

        {/* Filtros aplicados */}
        <div style={{ margin: '20px 0' }}>
          <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: 10 }}>
            {t('reportFilters')}:
          </Typography>
          {deviceStatus ? (
            <Chip
              label={`${t('deviceStatus')}: ${getStatusText(deviceStatus, t)}`}
              style={printStyles.filterChip}
            />
          ) : (
            <Chip
              label={t('sharedAll')}
              style={printStyles.filterChip}
            />
          )}
          <Chip
            label={`${t('deviceShowAll')}: ${formatBoolean(showAll, t)}`}
            style={printStyles.filterChip}
          />
        </div>

        {/* Tabela de dispositivos */}
        <Typography variant="h6" style={{ fontWeight: 'bold', margin: '20px 0 10px' }}>
          {t('deviceTitle')} ({filteredDevices.length})
        </Typography>
        <table style={printStyles.table}>
          <thead style={printStyles.tableHead}>
            <tr>
              {columns
                .filter(c => c.field !== 'actions' && !c.hide)
                .map((column) => (
                  <th key={column.field} style={printStyles.tableHeadCell}>
                    {column.headerName}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {filteredDevices.map((item) => (
              <tr key={item.id}>
                {columns
                  .filter(c => c.field !== 'actions' && !c.hide)
                  .map((column) => {
                    // Renderização especial para células com formatação
                    if (column.renderCell) {
                      return (
                        <td key={column.field} style={printStyles.tableCell}>
                          {column.renderCell({ row: item })}
                        </td>
                      )
                    }

                    // Renderização padrão
                    return (
                      <td key={column.field} style={printStyles.tableCell}>
                        {item[column.field] ?? ''}
                      </td>
                    )
                  })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'deviceTitle']}>
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Grupo de filtros por status */}
          <ButtonGroup variant="outlined" size="small" sx={{ mr: 2 }}>
            <Button
              onClick={() => setDeviceStatus('online')}
              variant={deviceStatus === 'online' ? 'contained' : 'outlined'}
              color="success"
              startIcon={<CheckCircleIcon />}
            >
              {t('deviceStatusOnline')} ({deviceStats.online})
            </Button>
            <Button
              onClick={() => setDeviceStatus('offline')}
              variant={deviceStatus === 'offline' ? 'contained' : 'outlined'}
              color="error"
              startIcon={<ErrorIcon />}
            >
              {t('deviceStatusOffline')} ({deviceStats.offline})
            </Button>
            <Button
              onClick={() => setDeviceStatus('unknown')}
              variant={deviceStatus === 'unknown' ? 'contained' : 'outlined'}
              color="warning"
              startIcon={<HelpIcon />}
            >
              {t('deviceStatusUnknown')} ({deviceStats.unknown})
            </Button>
            <Button
              onClick={() => setDeviceStatus('')}
              variant={deviceStatus === '' ? 'contained' : 'outlined'}
              color="primary"
            >
              {t('sharedAll')} ({deviceStats.total})
            </Button>
          </ButtonGroup>
        </Stack>

        <DataTable
          tableName='devices'
          columns={columns}
          rows={filteredDevices}
          loading={loading}
          filterable={true}
          enablePdfExport={manager}
          enableXlsxExport={manager}
          onPdfExport={handlePdfExport}
          onXlsxExport={handleExport}
        />
      </Box>

      <CollectionFab editPath='/settings/device' />
    </PageLayout>
  )
}

export default DevicesPage